import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Heart, ShoppingCart, ShieldCheck, Tag, ChevronLeft } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { ProductImage, ProductCard, getCategoryEmoji } from '../components/ui/index.jsx'
import { productService, promotionService } from '../services/api.js'
import { useCartStore, useWishlistStore, useAuthStore } from '../store/index.js'
import toast from 'react-hot-toast'

// ── Helpers ────────────────────────────────────────────────────────────────────
function norm(p) {
  return {
    id:            p.id,
    name:          p.description || p.product_name || p.stock_code,
    price:         p.inclprice ?? p.inclprice2 ?? 0,
    inStock:       true,
    primary_image: p.primary_image || null,
    image:         p.image || null,
    stock_code:    p.stock_code,
    category_id:   p.category_id,
    category_name: p.category_name || null,
    brand:         p.brand || null,
    packsize:      p.packsize || null,
    description:   p.long_description || p.description || '',
    on_promotion:  p.on_promotion || false,
  }
}

function packsizeLabel(ps) {
  if (!ps || ps <= 1) return null
  if (ps === 6)  return { label: '6-Pack',      emoji: '📦' }
  if (ps === 12) return { label: '12-Pack',     emoji: '📦' }
  if (ps === 24) return { label: 'Case of 24',  emoji: '🗃️' }
  if (ps === 48) return { label: 'Case of 48',  emoji: '🗃️' }
  if (ps >= 20)  return { label: `Case of ${ps}`, emoji: '🗃️' }
  return { label: `Pack of ${ps}`, emoji: '📦' }
}

// ── Pill component ─────────────────────────────────────────────────────────────
function Pill({ children, color = 'default' }) {
  const styles = {
    green:    'bg-green-900/30 text-green-400 border border-green-800/40',
    gray:     'bg-surface-elevated text-gray-400 border border-surface-border',
    teal:     'bg-primary/15 text-primary border border-primary/25',
    gold:     'bg-gold/15 text-gold border border-gold/30',
    default:  'bg-surface-elevated text-gray-300 border border-surface-border',
  }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-display font-semibold ${styles[color]}`}>
      {children}
    </span>
  )
}



// ── Promotions tab ─────────────────────────────────────────────────────────────
function PromotionsTab() {
  const { data: promos = [] } = useQuery({
    queryKey: ['promotions-public'],
    queryFn: () => promotionService.getAll().catch(() => []),
    staleTime: 5 * 60 * 1000,
  })
  const list = Array.isArray(promos) ? promos : (promos?.items ?? [])
  const now = new Date()
  const active = list.filter(p => {
    const start = p.starts_at ? new Date(p.starts_at) : null
    const end   = p.ends_at   ? new Date(p.ends_at)   : null
    if (start && start > now) return false
    if (end   && end   < now) return false
    return true
  })

  if (!active.length) return (
    <div className="text-center py-10">
      <span className="text-4xl block mb-3">🎁</span>
      <p className="text-sm font-display text-gray-400">No active promotions right now.</p>
      <p className="text-xs mt-1 text-gray-500">Check back soon for deals!</p>
    </div>
  )

  return (
    <div className="space-y-3">
      {active.map(p => {
        const end = p.ends_at ? new Date(p.ends_at) : null
        const msLeft = end ? end - now : null
        const daysLeft = msLeft ? Math.ceil(msLeft / 86400000) : null
        return (
          <div key={p.id} className="p-4 rounded-xl border border-gold/30 bg-gold/10 flex gap-3 items-start">
            <span className="text-2xl flex-shrink-0">🎁</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-display font-semibold text-white text-sm">{p.title}</h4>
              </div>
              {p.description && (
                <p className="text-xs text-gray-400 mt-1 leading-relaxed">{p.description}</p>
              )}
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                {p.discount_type === 'percentage' && p.discount_value && (
                  <span className="text-xs font-display font-semibold text-green-400">
                    {p.discount_value}% off
                  </span>
                )}
                {p.discount_type === 'fixed' && p.discount_value && (
                  <span className="text-xs font-display font-semibold text-green-400">
                    R{p.discount_value} off
                  </span>
                )}
                {daysLeft !== null && (
                  <span className={`text-xs font-display ${daysLeft <= 3 ? 'text-red-400 font-semibold' : 'text-gray-500'}`}>
                    {daysLeft <= 0 ? 'Ends today!' : `${daysLeft}d left`}
                  </span>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function ProductDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [qty, setQty] = useState(1)
  const [qtyDraft, setQtyDraft] = useState('1')

  useEffect(() => { setQtyDraft(String(qty)) }, [qty])
  const [activeTab, setActiveTab] = useState(0)
  const addItem = useCartStore(s => s.addItem)
  const count   = useCartStore(s => s.items.reduce((sum, i) => sum + (i.qty || 1), 0))
  const { toggle, has } = useWishlistStore()

  const [activeImg, setActiveImg] = useState(0)

  const { data: pd, isLoading } = useQuery({
    queryKey: ['product', id],
    queryFn: () => productService.getById(id),
    staleTime: 5 * 60 * 1000,
  })

  const { data: imagesData } = useQuery({
    queryKey: ['product', id, 'images'],
    queryFn: () => productService.getImages(id),
    staleTime: 10 * 60 * 1000,
    enabled: !!pd,
  })
  const extraImages = imagesData?.images || []

  const { data: relatedData } = useQuery({
    queryKey: ['products', 'related', pd?.category_id],
    queryFn: () => productService.getAll({ category: pd?.category_name, limit: 6 }),
    staleTime: 10 * 60 * 1000,
    enabled: !!pd?.category_name,
  })

  if (isLoading) return (
    <div className="min-h-screen bg-surface-dark animate-pulse pt-4 pb-8">
      <div className="h-10 mx-4 bg-surface-elevated rounded-xl mb-4" />
      <div className="h-64 mx-4 bg-surface-elevated rounded-2xl mb-4" />
      <div className="px-4 space-y-3">
        <div className="h-6 bg-surface-elevated rounded w-3/4" />
        <div className="h-4 bg-surface-elevated rounded w-1/2" />
      </div>
    </div>
  )

  if (!pd) return (
    <div className="min-h-screen bg-surface-dark flex items-center justify-center">
      <div className="text-center">
        <p className="font-heading text-white text-xl mb-2">Product not found</p>
        <button onClick={() => navigate('/shop')} className="btn-primary mt-4">Back to Shop</button>
      </div>
    </div>
  )

  const product  = norm(pd)
  const isWished = has(product.id)
  const uom      = packsizeLabel(product.packsize)
  const emoji    = getCategoryEmoji(product)

  const relatedRaw = Array.isArray(relatedData) ? relatedData : (relatedData?.items ?? [])
  const related = relatedRaw.filter(p => p.id !== pd.id).slice(0, 4).map(norm)

  const tabs = ['Description', 'Details', '🎁 Promos']

  const handleAddToCart = () => {
    addItem(product, qty)
    toast.success(`${qty > 1 ? qty + '×' : ''} Added to cart`.trim())
  }
  const handleBuyNow = () => {
    addItem(product, qty)
    navigate('/cart')
  }

  return (
    // No bottom nav on this page — own fixed CTA instead
    <div className="min-h-screen bg-surface-dark pb-8" style={{ paddingTop: 'calc(env(safe-area-inset-top,0px) + 0px)' }}>

      {/* Custom header — back + wishlist + cart */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <button onClick={() => navigate(-1)}
          className="w-10 h-10 bg-surface-card rounded-xl flex items-center justify-center border border-surface-border active:bg-surface-elevated">
          <ChevronLeft size={20} className="text-gray-300" />
        </button>
        <div className="flex gap-2">
          <button onClick={() => toggle(product)}
            className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-colors ${
              isWished ? 'bg-primary/20 border-primary/30' : 'bg-surface-card border-surface-border'
            }`}>
            <Heart size={18} className={isWished ? 'fill-primary text-primary' : 'text-gray-300'} />
          </button>
          <button onClick={() => navigate('/cart')}
            className="w-10 h-10 bg-surface-card rounded-xl flex items-center justify-center border border-surface-border relative active:bg-surface-elevated">
            <ShoppingCart size={18} className="text-gray-300" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center text-white text-[9px] font-bold font-display">
                {count > 9 ? '9+' : count}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Product image gallery */}
      <div className="mx-4 mb-4 space-y-2">
        {/* Main image */}
        <div className="relative h-64 bg-surface-elevated rounded-2xl overflow-hidden">
          {extraImages.length > 0 ? (
            <img
              src={extraImages[activeImg]?.url}
              alt={product.name}
              className="w-full h-full object-contain p-4"
              onError={e => { e.target.style.display='none' }}
            />
          ) : (
            <ProductImage product={product} className="w-full h-full object-contain p-4" />
          )}
          {product.on_promotion && (
            <div className="absolute top-3 left-3">
              <span className="bg-gold text-gray-900 text-[10px] font-display font-bold px-2 py-1 rounded-lg">
                🎁 On Promotion
              </span>
            </div>
          )}
          {extraImages.length > 1 && (
            <div className="absolute bottom-2 right-2 bg-black/50 rounded-lg px-2 py-0.5">
              <span className="text-white text-[10px] font-display">{activeImg + 1}/{extraImages.length}</span>
            </div>
          )}
        </div>
        {/* Thumbnail strip — only if 2+ images */}
        {extraImages.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {extraImages.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                className={`flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-colors ${
                  i === activeImg ? 'border-primary' : 'border-surface-border'
                }`}
              >
                <img
                  src={img.url}
                  alt=""
                  className="w-full h-full object-contain p-1 bg-surface-elevated"
                  onError={e => { e.target.style.display='none' }}
                />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 space-y-4">
        {/* Name + SKU */}
        <div>
          <h1 className="font-heading text-white text-xl leading-tight">{product.name}</h1>
          {product.stock_code && (
            <p className="text-xs text-gray-500 mt-1 font-display font-mono">SKU: {product.stock_code}</p>
          )}
        </div>

        {/* Badges / pills row */}
        <div className="flex flex-wrap gap-2">
          <Pill color="green">✅ In Stock</Pill>
          {product.category_name && (
            <Pill color="teal">{emoji} {product.category_name}</Pill>
          )}
          {product.brand && (
            <button
              onClick={() => navigate(`/shop?brand=${encodeURIComponent(product.brand)}`)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-display font-semibold bg-surface-elevated text-gray-300 border border-surface-border active:bg-surface-border">
              <Tag size={10} /> {product.brand}
            </button>
          )}
          {uom && <Pill color="default">{uom.emoji} {uom.label}</Pill>}
          {product.on_promotion && <Pill color="gold">🎁 On Promotion</Pill>}
        </div>

        {/* Price + rating */}
        <div className="flex items-center justify-between py-3 border-y border-surface-border/60">
          <div>
            <span className="font-heading text-gold text-3xl">R{parseFloat(product.price).toFixed(2)}</span>
            <span className="text-gray-500 text-xs font-display ml-2">incl. VAT</span>
            {product.packsize && product.packsize > 1 && product.price && (
              <p className="text-gray-500 text-xs font-display mt-0.5">
                ≈ R{(parseFloat(product.price) / product.packsize).toFixed(2)} per unit
              </p>
            )}
          </div>

        </div>

        {/* Qty stepper */}
        <div className="flex items-center gap-3">
          <span className="text-gray-400 text-sm font-display">Quantity</span>
          <div className="flex items-center bg-surface-elevated rounded-xl border border-surface-border overflow-hidden">
            <button onClick={() => setQty(q => Math.max(1, q - 1))}
              disabled={qty <= 1}
              className="w-10 h-10 flex items-center justify-center text-gray-300 active:bg-surface-border disabled:text-gray-600 text-xl leading-none transition-colors">−</button>
            <input
              type="number"
              min={1}
              max={999}
              value={qtyDraft}
              onChange={e => setQtyDraft(e.target.value)}
              onBlur={() => {
                const n = parseInt(qtyDraft, 10)
                if (!isNaN(n) && n >= 1) setQty(Math.max(1, Math.min(999, n)))
                else setQtyDraft(String(qty))
              }}
              onKeyDown={e => {
                if (e.key === 'Enter')  { e.target.blur() }
                if (e.key === 'Escape') { setQtyDraft(String(qty)); e.target.blur() }
              }}
              onClick={e => e.target.select()}
              className="w-10 h-10 text-center font-display font-semibold text-white bg-transparent focus:outline-none focus:bg-surface-border [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <button onClick={() => setQty(q => q + 1)}
              className="w-10 h-10 flex items-center justify-center text-gray-300 active:bg-surface-border text-xl leading-none transition-colors">+</button>
          </div>
        </div>

        {/* Inline Add to Cart + Buy Now */}
        <div className="flex gap-3 pt-1">
          <button onClick={handleAddToCart}
            className="btn-outline flex-1 flex items-center justify-center gap-2 py-3.5">
            <ShoppingCart size={16} /> Add to Cart
          </button>
          <button onClick={handleBuyNow} className="btn-primary flex-1 py-3.5">
            Buy Now
          </button>
        </div>

        {/* Description / Details / Promos tabs */}
        <div>
          <div className="flex gap-0 border-b border-surface-border">
            {tabs.map((tab, i) => (
              <button key={tab} onClick={() => setActiveTab(i)}
                className={`flex-1 py-2.5 text-sm font-display font-semibold transition-colors ${
                  activeTab === i ? 'text-primary border-b-2 border-primary -mb-px' : 'text-gray-400'
                }`}>
                {tab}
              </button>
            ))}
          </div>
          <div className="pt-4 text-gray-300 text-sm font-display leading-relaxed">
            {activeTab === 0 && <p>{product.description || 'No description available.'}</p>}
            {activeTab === 1 && (
              <div className="space-y-0">
                {[
                  ['Stock Code', product.stock_code],
                  ['Brand', product.brand],
                  ['Category', product.category_name],
                  ['Pack Size', product.packsize > 1 ? `${product.packsize} units` : null],
                  ['Price (Incl VAT)', `R${parseFloat(product.price).toFixed(2)}`],
                  ['Status', 'In Stock'],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="flex justify-between py-2.5 border-b border-surface-border/40">
                    <span className="text-gray-500">{k}</span>
                    <span className="text-white font-medium">{v}</span>
                  </div>
                ))}
              </div>
            )}
            {activeTab === 2 && <PromotionsTab />}
          </div>
        </div>

        {/* Trust badge */}
        <div className="grid grid-cols-1 gap-2">
          {[
            { icon: ShieldCheck, label: 'Authentic', sub: 'Verified products' },
          ].map(({ icon: Icon, label, sub }) => (
            <div key={label} className="bg-surface-card rounded-xl p-3 flex items-center gap-3 border border-surface-border">
              <Icon size={20} className="text-primary flex-shrink-0" />
              <div>
                <span className="text-white text-xs font-display font-semibold block">{label}</span>
                <span className="text-gray-500 text-[10px] font-display">{sub}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Related products */}
        {related.length > 0 && (
          <div>
            <h3 className="font-heading text-white text-base mb-3">More from this category</h3>
            <div className="grid grid-cols-2 gap-3">
              {related.map(p => (
                <ProductCard key={p.id} product={p} onClick={() => navigate(`/product/${p.id}`)} />
              ))}
            </div>
          </div>
        )}
      </div>


    </div>
  )
}
